import { Button } from '@mui/material'
import { useState } from 'react'
import { IconButton, InformationModal } from 'utoniq-ui'

import { Maybe } from '@/clients/utoniq-core/schema'
import { Head } from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { ArtistThemeProvider } from '@/components/styles'
import { useForm, useMessage, useRouter, useTranslation } from '@/core'
import { Template } from '@/core/architecture'
import { Place, Prediction } from '@/lib/googleMap'

import { FixedButtonArea, FixedButtonAreaProps } from './components'
import {
  CreateEventValue,
  defaultValues,
  EventCreateForm,
  EventCreateFormProps,
  schema,
} from './EventCreateForm'

export * from './fragments'

export type State = {
  artistId: string
  progress: EventCreateFormProps['fileUploadInfo']['progress']
  predictions: Prediction[]
  place: Maybe<Place>
  gpsClientInfo: EventCreateFormProps['gpsClientInfo']
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onSubmit: FixedButtonAreaProps['onSubmit']
  onDrop: EventCreateFormProps['fileUploadInfo']['onDrop']
  onPlaceSearch: (value: { keyword: string }) => Promise<void>
  onSelectPlace: (value: {
    placeId: string
    map: google.maps.Map
  }) => Promise<void>
  onRequestGPSPermission: () => void
}

export const ConsoleEventCreateTemplate: Template<State, Action> = ({
  state,
  onDrop,
  onSubmit,
  onRequestGPSPermission,
  onSelectPlace,
  onPlaceSearch,
}) => {
  const { t } = useTranslation('consoleEventCreate')
  const { router, routerNames } = useRouter()
  const { showMessage } = useMessage()
  const [openCancel, setOpenCancel] = useState<boolean>(false)

  const method = useForm<CreateEventValue>({ schema, defaultValues })

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <ConsoleBaseLayout
        margin
        header={{
          left: (
            <IconButton.Close
              edge="start"
              size="medium"
              color="white"
              onClick={() => {
                if (method.formState.isDirty) {
                  return setOpenCancel(true)
                }
                router.goBack()
              }}
            />
          ),
          title: t('title'),
          type: 'modal',
        }}
        customNavigation={
          <FixedButtonArea mode="create" method={method} onSubmit={onSubmit} />
        }
      >
        <Head title={routerNames.ConsoleShokufesEventCreate} />
        <EventCreateForm
          artistId={state.artistId}
          method={method}
          fileUploadInfo={{ progress: state.progress, onDrop }}
          predictions={state.predictions}
          place={state.place}
          onSelectPlace={onSelectPlace}
          onPlaceSearch={onPlaceSearch}
          gpsClientInfo={state.gpsClientInfo}
          onRequestGPSPermission={onRequestGPSPermission}
        />
        <InformationModal
          title={t('createCancelCreateEventModalTitle')}
          content={t('createCancelCreateEventModalDesc')}
          open={openCancel}
          onClose={() => setOpenCancel(false)}
          cancelText={t('close')}
          buttons={
            <Button
              color="primary"
              size="large"
              onClick={() => {
                showMessage(t('cancelCreateEventSuccess'), 'success')
                router.goBack()
              }}
              sx={{ minWidth: 212 }}
            >
              {t('createCancelCreateEventBtn')}
            </Button>
          }
        />
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}
